import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface ThemeColors {
  // Primary colors
  primaryColor: string;
  primaryHoverColor: string;
  
  // Text colors
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  
  // Background colors
  bgPage: string;
  bgCard: string;
  bgHover: string;
  
  // Border colors
  borderColor: string;
  
  // Success/Warning/Danger colors
  successBg: string;
  successText: string;
  successBorder: string;
  warningBg: string;
  warningText: string;
  warningBorder: string;
  dangerBg: string;
  dangerText: string;
  dangerBorder: string;
  infoBg: string;
  infoText: string;
  infoBorder: string;
}

export interface Theme {
  name: string;
  colors: ThemeColors;
}

const defaultTheme: Theme = {
  name: 'Default',
  colors: {
    primaryColor: '#2563eb', // blue-600
    primaryHoverColor: '#1d4ed8', // blue-700
    textPrimary: '#111827', // gray-900
    textSecondary: '#374151', // gray-700
    textTertiary: '#4b5563', // gray-600
    bgPage: '#f9fafb', // gray-50
    bgCard: '#ffffff', // white
    bgHover: '#f3f4f6', // gray-100
    borderColor: '#e5e7eb', // gray-200
    successBg: '#d1fae5', // green-100
    successText: '#065f46', // green-800
    successBorder: '#6ee7b7', // green-200
    warningBg: '#fef3c7', // yellow-100
    warningText: '#92400e', // yellow-800
    warningBorder: '#fde68a', // yellow-200
    dangerBg: '#fee2e2', // red-100
    dangerText: '#991b1b', // red-800
    dangerBorder: '#fecaca', // red-200
    infoBg: '#dbeafe', // blue-100
    infoText: '#1e40af', // blue-800
    infoBorder: '#bfdbfe', // blue-200
  },
};

const presetThemes: Theme[] = [
  defaultTheme,
  {
    name: 'Dark Blue',
    colors: {
      primaryColor: '#1e3a8a', // blue-900
      primaryHoverColor: '#1e40af', // blue-800
      textPrimary: '#111827',
      textSecondary: '#374151',
      textTertiary: '#4b5563',
      bgPage: '#f9fafb',
      bgCard: '#ffffff',
      bgHover: '#f3f4f6',
      borderColor: '#e5e7eb',
      successBg: '#d1fae5',
      successText: '#065f46',
      successBorder: '#6ee7b7',
      warningBg: '#fef3c7',
      warningText: '#92400e',
      warningBorder: '#fde68a',
      dangerBg: '#fee2e2',
      dangerText: '#991b1b',
      dangerBorder: '#fecaca',
      infoBg: '#dbeafe',
      infoText: '#1e40af',
      infoBorder: '#bfdbfe',
    },
  },
  {
    name: 'Purple',
    colors: {
      primaryColor: '#7c3aed', // violet-600
      primaryHoverColor: '#6d28d9', // violet-700
      textPrimary: '#111827',
      textSecondary: '#374151',
      textTertiary: '#4b5563',
      bgPage: '#f9fafb',
      bgCard: '#ffffff',
      bgHover: '#f3f4f6',
      borderColor: '#e5e7eb',
      successBg: '#d1fae5',
      successText: '#065f46',
      successBorder: '#6ee7b7',
      warningBg: '#fef3c7',
      warningText: '#92400e',
      warningBorder: '#fde68a',
      dangerBg: '#fee2e2',
      dangerText: '#991b1b',
      dangerBorder: '#fecaca',
      infoBg: '#ede9fe', // purple-100
      infoText: '#5b21b6', // purple-800
      infoBorder: '#ddd6fe', // purple-200
    },
  },
  {
    name: 'Emerald',
    colors: {
      primaryColor: '#059669', // emerald-600
      primaryHoverColor: '#047857', // emerald-700
      textPrimary: '#111827',
      textSecondary: '#374151',
      textTertiary: '#4b5563',
      bgPage: '#f9fafb',
      bgCard: '#ffffff',
      bgHover: '#f3f4f6',
      borderColor: '#e5e7eb',
      successBg: '#d1fae5',
      successText: '#065f46',
      successBorder: '#6ee7b7',
      warningBg: '#fef3c7',
      warningText: '#92400e',
      warningBorder: '#fde68a',
      dangerBg: '#fee2e2',
      dangerText: '#991b1b',
      dangerBorder: '#fecaca',
      infoBg: '#d1fae5', // emerald-100
      infoText: '#065f46', // emerald-800
      infoBorder: '#6ee7b7', // emerald-200
    },
  },
  {
    name: 'Rose',
    colors: {
      primaryColor: '#e11d48', // rose-600
      primaryHoverColor: '#be123c', // rose-700
      textPrimary: '#111827',
      textSecondary: '#374151',
      textTertiary: '#4b5563',
      bgPage: '#f9fafb',
      bgCard: '#ffffff',
      bgHover: '#f3f4f6',
      borderColor: '#e5e7eb',
      successBg: '#d1fae5',
      successText: '#065f46',
      successBorder: '#6ee7b7',
      warningBg: '#fef3c7',
      warningText: '#92400e',
      warningBorder: '#fde68a',
      dangerBg: '#fee2e2',
      dangerText: '#991b1b',
      dangerBorder: '#fecaca',
      infoBg: '#ffe4e6', // rose-100
      infoText: '#881337', // rose-800
      infoBorder: '#fecdd3', // rose-200
    },
  },
];

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  updateColors: (colors: Partial<ThemeColors>) => void;
  resetToDefault: () => void;
  presetThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('admin_theme');
    if (savedTheme) {
      try {
        return JSON.parse(savedTheme);
      } catch (e) {
        console.error('Failed to parse saved theme', e);
      }
    }
    return defaultTheme;
  });

  // Apply CSS variables whenever theme changes
  useEffect(() => {
    const root = document.documentElement;
    const colors = theme.colors;

    // Set CSS variables
    root.style.setProperty('--color-primary', colors.primaryColor);
    root.style.setProperty('--color-primary-hover', colors.primaryHoverColor);
    root.style.setProperty('--color-text-primary', colors.textPrimary);
    root.style.setProperty('--color-text-secondary', colors.textSecondary);
    root.style.setProperty('--color-text-tertiary', colors.textTertiary);
    root.style.setProperty('--color-bg-page', colors.bgPage);
    root.style.setProperty('--color-bg-card', colors.bgCard);
    root.style.setProperty('--color-bg-hover', colors.bgHover);
    root.style.setProperty('--color-border', colors.borderColor);
    root.style.setProperty('--color-success-bg', colors.successBg);
    root.style.setProperty('--color-success-text', colors.successText);
    root.style.setProperty('--color-success-border', colors.successBorder);
    root.style.setProperty('--color-warning-bg', colors.warningBg);
    root.style.setProperty('--color-warning-text', colors.warningText);
    root.style.setProperty('--color-warning-border', colors.warningBorder);
    root.style.setProperty('--color-danger-bg', colors.dangerBg);
    root.style.setProperty('--color-danger-text', colors.dangerText);
    root.style.setProperty('--color-danger-border', colors.dangerBorder);
    root.style.setProperty('--color-info-bg', colors.infoBg);
    root.style.setProperty('--color-info-text', colors.infoText);
    root.style.setProperty('--color-info-border', colors.infoBorder);

    // Save to localStorage
    localStorage.setItem('admin_theme', JSON.stringify(theme));
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const updateColors = (colors: Partial<ThemeColors>) => {
    setThemeState(prev => ({
      ...prev,
      name: 'Custom',
      colors: {
        ...prev.colors,
        ...colors,
      },
    }));
  };

  const resetToDefault = () => {
    setThemeState(defaultTheme);
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        updateColors,
        resetToDefault,
        presetThemes,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
